
import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface RoleContextType {
  isAdmin: boolean;
  loading: boolean;
  refetchRole: () => Promise<void>;
  forceAdminMode?: boolean;
  toggleAdminMode?: () => void;
}

const RoleContext = createContext<RoleContextType | undefined>(undefined);

// Check if we're in development mode
const isDevelopment = import.meta.env.DEV;

export const RoleProvider = ({ children }: { children: ReactNode }) => {
  const { user, isDevMockUser } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [devAdminMode, setDevAdminMode] = useState(() => {
    // Initialize from localStorage if available
    if (isDevelopment) {
      const saved = localStorage.getItem("devAdminMode");
      return saved ? saved === "true" : false;
    }
    return false;
  });

  const toggleAdminMode = () => {
    if (isDevelopment) {
      const newMode = !devAdminMode;
      setDevAdminMode(newMode);
      localStorage.setItem("devAdminMode", String(newMode));
      console.log(`Development admin mode ${newMode ? "enabled" : "disabled"}`);
    }
  };

  const fetchUserRole = async () => {
    if (!user) {
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // In development mode with mock user, always grant admin access
      if (isDevMockUser) {
        setIsAdmin(true);
        setLoading(false);
        console.log('🔐 Development mode: Mock admin access granted');
        return;
      }
      
      // For real users, use the security definer function to safely check admin status
      console.log('🔍 Checking admin role for user:', user.email);
      
      // Call the is_admin function that now exists in the database
      const { data, error } = await supabase.rpc('is_admin');
      
      if (error) {
        console.error('Error checking admin role:', error);
        setIsAdmin(false);
      } else {
        const adminStatus = Boolean(data);
        setIsAdmin(adminStatus);
        console.log('Admin status:', adminStatus ? 'GRANTED' : 'DENIED', 'for user:', user.email);
      }
      
    } catch (error) {
      console.error('Error in fetchUserRole:', error);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  // Separate effect for user changes to avoid multiple checks
  useEffect(() => {
    fetchUserRole();
  }, [user?.id]); // Only depend on user ID to avoid multiple checks

  // Separate effect for dev admin mode changes
  useEffect(() => {
    if (isDevelopment && user && !isDevMockUser) {
      // Only refetch if we're in development and have a real user
      fetchUserRole();
    }
  }, [devAdminMode]);

  const refetchRole = async () => {
    await fetchUserRole();
  };

  // Context value includes development mode controls when in development
  const contextValue: RoleContextType = {
    isAdmin,
    loading,
    refetchRole,
    ...(isDevelopment && { forceAdminMode: devAdminMode, toggleAdminMode })
  };

  return (
    <RoleContext.Provider value={contextValue}>
      {children}
    </RoleContext.Provider>
  );
};

export const useRole = () => {
  const context = useContext(RoleContext);
  if (context === undefined) {
    throw new Error("useRole must be used within a RoleProvider");
  }
  return context;
};
